// ゲーム状態の型定義。描画から完全に独立した純データ。
//
// 放置ゲームの中核は「経過時間 → 状態」の決定的純関数なので、
// ここにある型は描画・DOM・乱数を一切参照しない。乱数が要る来店抽選も、
// 乱数の種（seed）を状態に持たせることで「同じ state + 経過秒 → 同じ結果」を保つ。

import type { GardenGrid } from './garden';
import { createEmptyGarden, ensureEntrance } from './garden';
import { CUSTOMER_CATALOG } from './customers';
import type { Planting } from './plants';
import type { MenuState } from './menu';
import { createInitialMenu } from './menu';

/**
 * セーブデータのスキーマバージョン。マイグレーションの判定に使う。
 *
 * - v1: M1。chips / chipRate / lastTickMs のみ。
 * - v2: M2。庭グリッド `garden` を追加。
 * - v3: M3。客システム（seed / visitLog / dex）を追加し、カフェ被りセルの
 *        配置禁止に伴う返金マイグレーションを行う。
 * - v4: M4。盆栽（植物の実時間成長・樹形・剪定）の `plantings` を追加。
 * - v5: M5。カフェメニュー（ドリンク開発・本日のスペシャル）の `menu` を追加。
 * - v6: M5 後半。サウンドのミュート設定 `muted` を追加。
 * - v7: v0.5.1。グリッドを 8×8 → 12×12 に拡張（旧庭を中央 +2,+2 へ再配置）。
 *        評判システムの累計来店数 `totalVisits` を追加。
 * - v8: v0.6.0。予測不能・環境システム（ゆらぎ・昼夜・季節・隣接シナジー・ステータス画面）。
 *        新規の永続フィールドは無い（ゆらぎ/昼夜/季節はすべて seed＋絶対時刻からの導出で、
 *        季節の結晶は garden に乗る通常デコ）。スキーマ整合のためバージョンだけ進める。
 *        旧セーブはそのまま引き継ぐ（新デコは未配置・新挙動は次回 simulate から有効）。
 * - v9: v0.7.0。客の動線＋近接効果＋タイルのアップグレード。各 GardenCell に level（1..5）
 *        を補完（旧セルは level=1）。入口ゲート（entrance）を島手前の固定セルに自動配置する
 *        （既存セーブにも必ず 1 つ補う）。seat/path/ambiance はすべて庭の構成からの純計算で、
 *        新規の永続フィールドは level と入口デコのみ。
 * - v10: v0.7.1。多島化（隣の島＋スワイプ移動）。トップレベルの garden/plantings を廃止し、
 *        島の配列 `islands`（index 0 = ホーム島＝カフェ・入口がある島）に移す。`unlockedIslands`
 *        （アンロック済み島数、初期 1）を追加。拡張島（index ≥1）はカフェも入口も無く、客は
 *        来ない（眺めて育てる庭）。来店経済はホーム島に一本化するが、来店レートの属性は全島
 *        合算（totalGardenAttributes）。旧 v1〜v9 のセーブは garden/plantings をホーム島(0)に
 *        包んで移行する（ホーム島のみ入口/カフェ被りの evict を適用）。
 * - v11: v0.7.3。島ごとの形状バリアント（round/oval/hex/clover/teardrop）を導入。形状は
 *        島 index から決定的に導出（shapeForIslandIndex）するので**新規の永続フィールドは無い**。
 *        ホーム島(0)は round のまま不変。既存の拡張島(≥1)は、それぞれの形状の述語で reconcile
 *        し、形状外に出たデコは全額返金して撤去する（移行は reconcile のみ）。
 * - v12: v0.7.4。形状ラインアップを更新（island1: oval→square / island2: hex→twin。island3=clover・
 *        island4=teardrop は不変）。twin は「丸 2 つ＋橋」、square は正方形。形状は引き続き island
 *        index 導出なので**新規の永続フィールドは無い**。既存の拡張島(≥1)を新しい形状の述語で
 *        reconcile し、新形状の外に出たデコは全額返金して撤去する（v11 と同じ evictOutsideShape を
 *        新ラインアップで適用するだけ）。ホーム島(0)は round のまま不変。
 * - v13: v0.7.5。twin（島2）の幾何を変更（2 円を離し centerX 2.7→3.0・radius 2.5→2.0、橋モデルで
 *        連結）。形状述語が変わるので既存の twin 島を新ジオメトリで reconcile（migrate は毎ロード
 *        buildExtensionIsland→evictOutsideShape を通すため、形状外デコは自動で全額返金）。
 */
export const SAVE_VERSION = 13;

/** 島数の上限（ホーム + 拡張4 = 5、v0.7.1）。 */
export const MAX_ISLANDS = 5;

/** 新規ゲームの初期チップ残高（デコをいくつか試せる程度）。 */
export const INITIAL_CHIPS = 50;

/** 新規ゲームの初期 PRNG seed。固定値にして新規プレイを再現可能にする。 */
export const INITIAL_SEED = 0x9e3779b9;

/** 訪問ログとして保持する直近件数の上限。 */
export const VISIT_LOG_LIMIT = 30;

/** 1 件の来店記録（直近 N 件保持）。 */
export interface VisitLogEntry {
  /** 来た客の ID（CUSTOMER_CATALOG の id）。 */
  customerId: string;
  /** 来店した実時刻（エポックミリ秒、lastTickMs 基準で算出）。 */
  atMs: number;
  /** その来店で落としたチップ。 */
  tip: number;
}

/** 図鑑 1 種ぶんの記録。 */
export interface DexEntry {
  /** 初訪問したか（false の間はシルエット表示）。 */
  discovered: boolean;
  /** 累計訪問数。 */
  count: number;
  /** 初訪問の実時刻（未発見は null）。 */
  firstAtMs: number | null;
}

/** 図鑑全体: 客 ID → 記録。 */
export type Dex = Record<string, DexEntry>;

/**
 * 1 つの島（v0.7.1）。庭グリッドと、その島の植物の生育状態を持つ。
 *
 * 島 index 0 = ホーム島（カフェ・入口があり客が来る唯一の島）。index ≥1 = 拡張島
 * （カフェも入口も無い「眺めて育てる庭」。客は来ないが、庭デコの属性は来店レートに
 * 全島合算で効く＝totalGardenAttributes）。庭の純関数（placeDeco 等）は対象島 index を
 * 末尾オプション引数（既定 0）で受けて `state.islands[islandIndex]` を読み書きする。
 */
export interface Island {
  /** この島の庭グリッド（GRID_SIZE×GRID_SIZE の行優先 1 次元配列、null = 空）。 */
  garden: GardenGrid;
  /** この島の盆栽の生育状態。キーはセル index の文字列、値は Planting。 */
  plantings: Record<string, Planting>;
}

export interface GameState {
  /** スキーマバージョン。 */
  version: number;
  /** 貯まっているチップ（💰）。小数で保持し表示時に floor する。 */
  chips: number;
  /** 1 秒あたりのチップ生成レート。M2 以降は庭の属性から導出する。 */
  chipRate: number;
  /** この状態が最後に精算された実時刻（エポックミリ秒）。 */
  lastTickMs: number;
  /**
   * 島の配列（v0.7.1）。index 0 = ホーム島（カフェ・入口あり）。index ≥1 = 拡張島。
   * 各島は庭グリッドと植物の生育状態を持つ。長さは unlockedIslands と一致する
   * （アンロック済みの島だけ実体を持つ）。
   */
  islands: Island[];
  /**
   * アンロック済みの島数（v0.7.1、初期 1 = ホームのみ）。[1, MAX_ISLANDS] にクランプ。
   * islands.length と一致させる（unlockNextIsland が両方を伸ばす）。
   */
  unlockedIslands: number;
  /**
   * 来店抽選用の PRNG seed（32bit）。simulate が来店判定を進めるたびに更新する。
   * これを状態に持つことで simulate は決定的純関数のまま乱数を扱える。
   */
  seed: number;
  /** 直近 VISIT_LOG_LIMIT 件の来店記録（新しいものが末尾）。 */
  visitLog: VisitLogEntry[];
  /** 図鑑（客 ID → 発見/累計）。 */
  dex: Dex;
  /**
   * 来店判定の未処理経過秒（0〜TICK_SEC 未満のキャリー）。
   *
   * 来店抽選は固定刻み（TICK_SEC = 60 秒）で進める。フレーム呼び出しの細かい dt
   * を取りこぼさず、かつ「刻みをどう分割しても結果が一致する」決定性を保つために、
   * 1 刻みに満たない端数をここに溜める。
   */
  visitAccumSec: number;
  /**
   * カフェメニューの状態（M5）。開発済みドリンクと「本日のスペシャル」。
   * スペシャルの属性は gardenAttributes に加算され、来店客のチップにも小ボーナスが乗る。
   */
  menu: MenuState;
  /**
   * サウンドのミュート設定（M5 後半）。true なら BGM・SE を鳴らさない。
   * UI のミュートトグルで切り替え、セーブに永続化する。
   */
  muted: boolean;
  /**
   * 累計来店数（v0.5.1 評判システム）。simulate が来店成立のたびに加算する
   * （visitLog は直近 N 件で丸められるため別に持つ）。これと図鑑の発見数から
   * 「評判」を導出し、来店頻度・同時滞在の上限を成長させる。決定性のため状態に持つ。
   */
  totalVisits: number;
}

/** 全客種ぶんの未発見 Dex を作る。 */
export function createEmptyDex(): Dex {
  const dex: Dex = {};
  for (const c of CUSTOMER_CATALOG) {
    dex[c.id] = { discovered: false, count: 0, firstAtMs: null };
  }
  return dex;
}

/**
 * 新規ゲームの初期状態。
 * @param nowMs 現在時刻（エポックミリ秒）。テストのため明示的に渡す。
 */
export function createInitialState(nowMs: number): GameState {
  return {
    version: SAVE_VERSION,
    chips: INITIAL_CHIPS,
    // M1 は「チップが時間で貯まるだけ」の最小実装。基準 1 チップ/秒。
    chipRate: 1,
    lastTickMs: nowMs,
    // ホーム島（index 0）のみで開始。入口ゲートを島手前の固定セルに自動配置（v0.7.0）。
    islands: [{ garden: ensureEntrance(createEmptyGarden()), plantings: {} }],
    unlockedIslands: 1,
    seed: INITIAL_SEED,
    visitLog: [],
    dex: createEmptyDex(),
    visitAccumSec: 0,
    menu: createInitialMenu(),
    muted: false,
    totalVisits: 0,
  };
}
